import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Zap, Droplets, Star, Gift } from "lucide-react";
import { OrbCanvas } from "./OrbCanvas";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1783946194631-33b4da1be39f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHx0b3klMjBibGFzdGVyJTIwb3JhbmdlJTIwbmVvbiUyMHdhdGVyJTIwZ3VuJTIwcHJvZHVjdCUyMHNob3R8ZW58MHx8fHwxNzkwODIyMzc4fDA&ixlib=rb-4.1.0&q=85";

const LINES = ["LADEN.", "RICHTEN.", "SPLAT!"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-16">
      <OrbCanvas />
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#00FF66]/10 blur-[120px] pointer-events-none"
      />
      <motion.div
        style={{ y: glowY }}
        className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF087B]/10 blur-[130px] pointer-events-none"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-[#00FF66]/10 text-[#00FF66] border border-[#00FF66]/30 px-4 py-1.5 rounded-full text-xs font-mono-brand uppercase tracking-widest mb-8"
          >
            <Zap className="w-3.5 h-3.5" />
            De nr. 1 gel blaster shop van de Benelux
          </motion.div>

          <h1 className="font-display text-7xl sm:text-8xl lg:text-9xl leading-[0.88] tracking-wide uppercase" data-testid="hero-title">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-gradient-splat" : "text-white"}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.16, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mt-7 text-base sm:text-lg text-slate-300 max-w-md leading-relaxed"
            data-testid="hero-subtitle"
          >
            Gelvaro blasters schieten zachte water-orbs die bij impact uiteenspatten. Geen pijn, geen troep — alleen pure adrenaline. Bij elke blaster krijg je <span className="text-[#00E5FF] font-bold">10.000 gel orbs gratis</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.6 }}
            className="mt-7"
            data-testid="hero-bundle-sticker"
          >
            <div className="inline-flex items-start gap-3 bg-gradient-to-r from-[#FF087B]/15 via-[#00E5FF]/10 to-[#00FF66]/15 border border-[#FF087B]/40 rounded-2xl px-5 py-3.5 backdrop-blur-sm shadow-[0_0_30px_rgba(255,8,123,0.18)] max-w-md">
              <motion.div
                animate={{ rotate: [0, -12, 12, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className="shrink-0 w-10 h-10 rounded-xl bg-[#FF087B]/20 flex items-center justify-center"
              >
                <Gift className="w-5 h-5 text-[#FF087B]" />
              </motion.div>
              <div>
                <p className="font-mono-brand text-[10px] uppercase tracking-widest text-[#FF087B] mb-0.5">Bundelvoordeel</p>
                <p className="text-sm font-bold text-white leading-snug">
                  Vanaf 3 blasters <span className="text-[#00FF66]">€10 korting per stuk</span>
                </p>
                <p className="text-sm font-bold text-white leading-snug">
                  Bij 6 blasters een <span className="text-[#00E5FF]">gratis Bundel Pack</span> erbij
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#blasters"
              data-testid="hero-cta-shop"
              className="bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider px-8 py-4 rounded-full glow-lime hover:glow-lime-strong hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Shop de collectie
            </a>
            <a
              href="#waarom"
              data-testid="hero-cta-waarom"
              className="text-slate-300 hover:text-white font-bold px-2 py-4 underline decoration-[#FF087B] decoration-2 underline-offset-8 transition-colors"
            >
              Waarom Gelvaro?
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400"
          >
            <span className="flex items-center gap-2" data-testid="hero-stat-speed">
              <Zap className="w-4 h-4 text-[#00FF66]" /> Tot 18 schoten/sec
            </span>
            <span className="flex items-center gap-2" data-testid="hero-stat-orbs">
              <Droplets className="w-4 h-4 text-[#00E5FF]" /> 10.000 orbs inbegrepen
            </span>
            <span className="flex items-center gap-2" data-testid="hero-stat-rating">
              <Star className="w-4 h-4 text-[#FF087B] fill-[#FF087B]" /> 4.9/5 uit 2.300+ reviews
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imageY }} className="relative">
            <div className="spotlight-frame rounded-[2rem] border border-slate-700/60 rotate-2 shadow-[0_0_80px_rgba(0,229,255,0.15)]">
              <img
                src={HERO_IMAGE}
                alt="Gelvaro gel blaster in actie"
                data-testid="hero-image"
                className="w-full h-[560px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090D16]/80 via-transparent to-transparent" />
            </div>
            <motion.div
              className="absolute -bottom-6 -left-8 bg-slate-900/90 backdrop-blur-xl border border-[#00FF66]/30 rounded-2xl px-5 py-4 glow-lime"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <p className="font-mono-brand text-[10px] uppercase tracking-widest text-[#00FF66]">Bestseller</p>
              <p className="font-display text-2xl text-white tracking-wide">Hydro Pulse X-1</p>
              <p className="text-sm font-bold text-[#00E5FF]">€49,95</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
